import React, { useEffect, useState, useContext, Fragment } from 'react';
import SearchContext from '../../state/context';
import {
  Circle,
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow
} from '@react-google-maps/api';
import {
  getGoogleMapsInfoWindowContentString,
  unitConversion,
  setCurrentCommunityCookie
} from '../../helpers';
import { stylers } from '../../../constants';

const containerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '600px'
};

const GoogleMapResultsDisplay = ({
  linkToAnchor = null,
  center,
  radiusInMiles,
  zoomControl = false,
  zoomPosition,
  zoom,
  streetViewControl = false,
  mapTypeControl = false,
  scaleControl = false,
  fullscreenControl = false,
  disableDoubleClickZoom = false,
  draggable = true,
  circleDebug = false,
  apiKey,
  showInfoWindows = true,
  showMarkerLabel = false
}) => {
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [circle, setCircle] = useState(null);
  const [map, setMap] = useState(null);
  const [iconCircle, setIconCircle] = useState(null);
  const [iconNormal, setIconNormal] = useState(null);
  const [iconHover, setIconHover] = useState(null);
  const [results, setResults] = useState([]);
  const [mapCenter, setMapCenter] = useState(center);

  const {
    resultsListController,
    waitingForGeolocation,
    hoveredResult = null,
    onMarkerClick = null
  } = useContext(SearchContext);

  const [state, setState] = useState(resultsListController.state);

  useEffect(() => {
    resultsListController.subscribe(() => {
      setState(resultsListController.state);
    });
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey
  });

  const getBoundsFromMarkers = resultsArray => {
    if (map && resultsArray.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      resultsArray.forEach(result => {
        if (result.raw.ssl_latitude_d && result.raw.ssl_longitude_d) {
          bounds.extend({
            lat: result.raw.ssl_latitude_d,
            lng: result.raw.ssl_longitude_d
          });
        }
      });
      if (resultsArray.length > 0) map.fitBounds(bounds);
      else setMapCenter(bounds.getCenter());
    }
  };

  const fitMapBounds = () => {
    if (circle && map) {
      if (radiusInMiles > 0)
        map.fitBounds(circle.getBounds(), {
          top: 0,
          right: 0,
          left: 0,
          bottom: 0
        });
    }
  };

  const handleActiveMarker = marker => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(marker);
  };

  useEffect(() => {
    const newResults = state.results.map(result => {
      result = { ...result };
      if (result.raw.distance)
        result = {
          ...result,
          raw: {
            ...result.raw,
            distance: (result.raw.distance / 1610).toFixed(2)
          }
        };
      if (result.raw.ssl_latitude_d && result.raw.ssl_longitude_d) {
        if (result.raw.distance_search || result.raw.distance) {
          const calcDistance = result.raw.distance_search
            ? result.raw.distance_search
            : result.raw.distance;
          const convertedDistance = unitConversion(calcDistance, 'mi');
          result = {
            ...result,
            raw: {
              ...result.raw,
              distance_search: convertedDistance
            }
          };
        }

        const infoWindowContentString = getGoogleMapsInfoWindowContentString({
          linkToAnchor,
          linkWholeCard: true,
          showDistance: false,
          underlineTitle: true,
          result: result
        });

        result = { ...result, infoWindowContentString };
        result = { ...result, icon: iconNormal };
      }
      return result;
    });
    setActiveMarker(null);
    setResults(newResults);
    if (state.results.length > 0 && radiusInMiles !== 0) {
      getBoundsFromMarkers(newResults);
    } else getBoundsFromMarkers(newResults);
  }, [state, iconNormal]);

  useEffect(() => {
    if (circle && map) {
      map.addListener('click', () => {
        setActiveMarker(null);
        //map.panTo(mapCenter);
      });
    }

    return () => {
      if (circle && map) {
        window.google.maps.event.clearInstanceListeners(map);
      }
    };
  }, [circle, map, mapCenter]);

  const onCircleLoad = React.useCallback(circle => {
    setCircle(circle);
  }, []);

  const onLoad = React.useCallback(map => {
    setIconCircle({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: 'white',
      fillOpacity: 1,
      strokeWeight: 0,
      scale: 10,
      anchor: new window.google.maps.Point(0, 3)
    });
    setIconNormal({
      path: 'M20 0A20.271 20.271 0 0 0 0 20.405c0 16.077 18.615 31.712 19.481 32.418A1.214 1.214 0 0 0 20 53a1.214 1.214 0 0 0 .519-.177C21.3 52.205 40 36.482 40 20.405A20.271 20.271 0 0 0 20 0Z',
      fillColor: '#037eab',
      strokeColor: 'white',
      fillOpacity: 1,
      scale: 1,
      anchor: new window.google.maps.Point(20, 53)
    });
    setIconHover({
      path: 'M20 0A20.271 20.271 0 0 0 0 20.405c0 16.077 18.615 31.712 19.481 32.418A1.214 1.214 0 0 0 20 53a1.214 1.214 0 0 0 .519-.177C21.3 52.205 40 36.482 40 20.405A20.271 20.271 0 0 0 20 0Z',
      fillColor: '#f46b16',
      strokeColor: 'white',
      fillOpacity: 1,
      scale: 1,
      anchor: new window.google.maps.Point(20, 53)
    });
    setMap(map);
  }, []);

  const onUnmount = React.useCallback(_map => {
    setMap(null);
  }, []);

  useEffect(() => {
    const newResults = [];
    results.map(result => {
      if (result.raw.ssl_communityid === hoveredResult?.raw.ssl_communityid) {
        result.icon = iconHover;
      } else {
        result.icon = iconNormal;
      }
      newResults.push(result);
    });
    setResults(newResults);
  }, [hoveredResult]);

  useEffect(() => {
    const handleResize = () => {
      fitMapBounds();
      getBoundsFromMarkers(results);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  if (waitingForGeolocation) {
    return (
      <div className="headless-loader">
        <div className="headless-loader__inner"></div>
      </div>
    );
  }

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={mapZoom}
      options={{
        zoomControl,
        zoomControlOptions: {
          position:
            window.google?.maps.ControlPosition[zoomPosition || 'TOP_LEFT']
        },
        streetViewControl,
        mapTypeControl,
        scaleControl,
        fullscreenControl,
        disableDoubleClickZoom,
        draggable,
        styles: stylers
      }}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      <Circle
        onLoad={onCircleLoad}
        center={mapCenter}
        radius={radiusInMiles * 1610}
        options={{
          fillColor: 'coral',
          fillOpacity: 0.3,
          strokeWeight: 2,
          strokeColor: 'coral',
          clickable: false,
          editable: true,
          zIndex: 1,
          visible: circleDebug
        }}
      />

      {map &&
        circle &&
        iconCircle &&
        iconNormal &&
        iconHover &&
        results.map((result, index) => (
          <Fragment key={result.raw.ssl_communityid}>
            {result.raw.ssl_latitude_d && result.raw.ssl_longitude_d && (
              <Marker
                position={{
                  lat: result.raw.ssl_latitude_d,
                  lng: result.raw.ssl_longitude_d
                }}
                icon={result.icon && { ...result.icon }}
                onMouseOver={() => {
                  if (onMarkerClick) return;
                  setResults(
                    results.map(r => {
                      if (
                        result.raw.ssl_communityid === r.raw.ssl_communityid
                      ) {
                        r.icon = iconHover;
                      }
                      return r;
                    })
                  );
                }}
                onMouseOut={() => {
                  if (onMarkerClick) return;
                  setResults(
                    results.map(r => {
                      if (
                        result.raw.ssl_communityid === r.raw.ssl_communityid
                      ) {
                        r.icon = iconNormal;
                      }
                      return r;
                    })
                  );
                }}
                onClick={() => {
                  if (onMarkerClick) onMarkerClick(result);
                  if (showInfoWindows)
                    handleActiveMarker(result.raw.ssl_communityid);
                }}
              >
                {activeMarker === result.raw.ssl_communityid ? (
                  <InfoWindow
                    onCloseClick={() => {
                      setActiveMarker(null);
                    }}
                  >
                    <div
                      onClick={() => {
                        setCurrentCommunityCookie(result);
                      }}
                      dangerouslySetInnerHTML={{
                        __html: result.infoWindowContentString
                      }}
                    />
                  </InfoWindow>
                ) : null}
              </Marker>
            )}
            {result.raw.ssl_latitude_d && result.raw.ssl_longitude_d && (
              <Marker
                key={result.raw.ssl_communityid}
                position={{
                  lat: result.raw.ssl_latitude_d,
                  lng: result.raw.ssl_longitude_d
                }}
                label={
                  showMarkerLabel
                    ? { text: `${index + 1}`, color: '#000000' }
                    : null
                }
                icon={result.icon && { ...iconCircle }}
                onClick={() => {
                  if (onMarkerClick) onMarkerClick(result);
                  if (showInfoWindows)
                    handleActiveMarker(result.raw.ssl_communityid);
                }}
                onMouseOver={() => {
                  if (onMarkerClick) return;
                  setResults(
                    results.map(r => {
                      if (
                        result.raw.ssl_communityid === r.raw.ssl_communityid
                      ) {
                        r.icon = iconHover;
                      }
                      return r;
                    })
                  );
                }}
                onMouseOut={() => {
                  if (onMarkerClick) return;
                  setResults(
                    results.map(r => {
                      if (
                        result.raw.ssl_communityid === r.raw.ssl_communityid
                      ) {
                        r.icon = iconNormal;
                      }
                      return r;
                    })
                  );
                }}
              />
            )}
          </Fragment>
        ))}
    </GoogleMap>
  ) : (
    <div className="headless-loader">
      <div className="headless-loader__inner"></div>
    </div>
  );
};

export default React.memo(GoogleMapResultsDisplay);
